import React from "react";
import { Box } from "@mui/material";
import { dt } from "../../../shared/themes/redesignTokens";
import useTranslation from "../../../shared/hooks/useTranslation";

export interface KioskTileProps {
  icon: React.ReactNode;
  label: string;
  badge?: number;
  accentColor: string;
  disabled?: boolean;
  comingSoon?: boolean;
  onClick?: () => void;
  tinted?: boolean;
  tintBg?: string;
  index?: number;
}

const KioskTile: React.FC<KioskTileProps> = ({
  icon,
  label,
  badge,
  accentColor,
  disabled = false,
  comingSoon = false,
  onClick,
  tinted = false,
  tintBg,
  index = 0,
}) => {
  const { translate } = useTranslation("public");
  const isDisabled = disabled || comingSoon;

  return (
    <Box
      onClick={isDisabled ? undefined : onClick}
      sx={{
        minHeight: "120px",
        padding: "20px",
        borderRadius: dt.radius,
        background: tinted && tintBg ? tintBg : dt.cardBg,
        border: `1px solid ${dt.cardBorder}`,
        borderLeft: `3px solid ${accentColor}`,
        boxShadow: dt.cardShadow,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        cursor: isDisabled ? "default" : "pointer",
        opacity: isDisabled ? 0.5 : 1,
        position: "relative",
        transition: "transform 150ms ease",
        animation: `tileEnter 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.06}s both`,
        "&:active": isDisabled
          ? {}
          : {
              transform: "scale(0.97)",
            },
        userSelect: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            color: tinted ? accentColor : dt.textSecondary,
            "& .MuiSvgIcon-root": { fontSize: "36px" },
          }}
        >
          {icon}
        </Box>

        {badge !== undefined && badge > 0 && (
          <Box
            sx={{
              minWidth: "20px",
              padding: "0 6px",
              borderRadius: "10px",
              backgroundColor: accentColor,
              color: "#FFFFFF",
              fontSize: "12px",
              fontWeight: 700,
              fontFamily: dt.fontMono,
              textAlign: "center",
              lineHeight: "20px",
            }}
          >
            {badge}
          </Box>
        )}
      </Box>

      <Box sx={{ mt: "auto", pt: 1 }}>
        <Box
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            fontFamily: dt.font,
            color: dt.textPrimary,
            letterSpacing: "0.1px",
          }}
        >
          {label}
        </Box>
        {comingSoon && (
          <Box
            sx={{
              fontSize: "11px",
              fontWeight: 500,
              fontFamily: dt.font,
              color: dt.textMuted,
              mt: 0.25,
            }}
          >
            {translate("kioskComingSoon")}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default KioskTile;
